/* /api/field-audits/* - 15-api-contract.md sections 3.15, 3.15a and 3.16: the
 * caller sends trees and missed trees to themselves, and one somebody holds is
 * skipped, with who; the phone reads the trees they hold; a tree is released;
 * the phone lists the trees near one it holds and adds them; an audit is
 * synced from the phone; the tenant's missed trees are listed, and one is read
 * and placed.
 * No limit on a send's size here: the route's refusal says it (refusal.ts),
 * so the number lives in one place.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiFetch } from "../auth";
import { fetchJson, fetchPage, postJson, putJson, useAllPages, type PageArgs } from "./envelope";
import type { AssignmentRecord, MissedTreeDetailRecord, MissedTreeRecord, PlacementIds, SendResult, TreeRecord } from "./types";

const OPEN_LIST = ["field-audits", "assignments"] as const;
const MISSED_LIST = ["field-audits", "missed"] as const;

/* Every read that shows a tree's audit state: the list, its counts, each
 * tree's detail, the missed list and each missed tree's detail, and the open
 * list the phone reads, with the nearby trees keyed under it. */
export function changedHolds(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["mirror", "trees"] }),
    queryClient.invalidateQueries({ queryKey: ["mirror", "tree-facets"] }),
    queryClient.invalidateQueries({ queryKey: ["mirror", "tree"] }),
    queryClient.invalidateQueries({ queryKey: MISSED_LIST }),
    queryClient.invalidateQueries({ queryKey: OPEN_LIST }),
  ]);
}

/* Our tree ids, missed tree ids, or both: each list is optional to the
 * route, so a send carries only the ones it has. */
export interface SendIds {
  tree_ids?: string[];
  missed_tree_ids?: string[];
}

export function useSendForAudit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: SendIds) =>
      postJson<SendResult>("/api/field-audits/send", ids, (status) => `the trees could not be sent for audit (${status})`),
    onSuccess: () => changedHolds(queryClient),
  });
}

/* One page of the tenant's missed trees, newest first. `total` is always
 * given, so the first page is the Missed chip's count as well (15 section
 * 3.16). */
export function useMissedTrees({ cursor = null, limit }: PageArgs = {}) {
  return useQuery({
    queryKey: [...MISSED_LIST, cursor, limit],
    queryFn: () => fetchPage<MissedTreeRecord>("/api/field-audits/missed", {}, cursor, limit),
  });
}

/* The caller's open assignments, oldest first: what the phone pulls (`27`
 * section 8). Every page, so the phone never shows part of what it holds as
 * though it were all of it. */
export function useOpenAssignments() {
  return useAllPages<AssignmentRecord>(OPEN_LIST, "/api/field-audits/assignments", {});
}

/* Releases an assignment by its id, for its holder or an administrator; the
 * route decides which may (15 section 3.15). */
export function useReleaseAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      postJson<AssignmentRecord>(
        `/api/field-audits/assignments/${encodeURIComponent(assignmentId)}/release`,
        {},
        (status) => `the tree could not be released (${status})`,
      ),
    onSuccess: () => changedHolds(queryClient),
  });
}

/* One missed tree, with its later audits, its open assignment and what its pin
 * suggests (15 section 3.16). Under the missed list's key, so whatever
 * refreshes the list refreshes it too. */
export function useMissedTree(id: string | undefined) {
  return useQuery({
    queryKey: [...MISSED_LIST, id],
    queryFn: () =>
      fetchJson<MissedTreeDetailRecord>(
        `/api/field-audits/missed/${encodeURIComponent(id!)}`,
        (status) => `the missed tree could not be read (${status})`,
      ),
    enabled: id !== undefined,
  });
}

/* Places a missed tree on the web (15 section 3.16). The route runs the one
 * placement check the sync runs, and says which part it refused. */
export function usePlaceMissedTree(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (placement: PlacementIds) =>
      putJson<MissedTreeRecord>(
        `/api/field-audits/missed/${encodeURIComponent(id)}/placement`,
        placement,
        (status) => `the missed tree could not be placed (${status})`,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MISSED_LIST }),
  });
}

const nearbyPath = (assignmentId: string) =>
  `/api/field-audits/assignments/${encodeURIComponent(assignmentId)}/nearby`;

/* The trees near the one an assignment holds, nearest first, each with who
 * holds it (15 section 3.15). Keyed under the open list, so whatever changes
 * who holds a tree - an add, a send, a release - reads them again. */
export function useNearbyTrees(assignmentId: string) {
  return useAllPages<TreeRecord>([...OPEN_LIST, assignmentId, "nearby"], nearbyPath(assignmentId), {});
}

/* Holds a tree near the one an assignment holds for the caller, as a send
 * holds one; the route refuses a tree it would not list, or one held. */
export function useAddNearby() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assignmentId, treeId }: { assignmentId: string; treeId: string }) =>
      postJson<AssignmentRecord>(
        nearbyPath(assignmentId),
        { tree_id: treeId },
        (status) => `the tree could not be added (${status})`,
      ),
    onSuccess: () => changedHolds(queryClient),
  });
}

/* One audit synced from the phone (15 section 3.15a), answered as it came:
 * the caller reads the status, because which refusal it is decides whether the
 * work is sent again, kept, or waits for a sign-in (field/sync.ts). */
export function postAudit(form: FormData): Promise<Response> {
  return apiFetch("/api/field-audits/audits", { method: "POST", body: form });
}
